import { zodResolver } from '@hookform/resolvers/zod'
import { ReactNode, useCallback, useMemo } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

import { PasswordInputFormField } from '@/components/form'
import { TextInputFormField } from '@/components/form/text-input-form-field'
import { TabActions } from '@/components/layout/tab-card'
import { toastFactory } from '@/factories'
import { getApiErrorMessage } from '@/utils/error'

import { FormChangeFields, getFormChanges } from '../../../../shared/form-changes'
import { ProviderCard } from './provider-card'
import { ProviderField } from './provider-field'
import { SecretValue } from './secret-value'
import { maskSecret, ProviderDirtyChange, useProviderEdit } from './use-provider-edit'

export interface CredentialField {
  error: string
  label: string
  name: string
  placeholder: string
  secret?: boolean
}

export interface CredentialsCardProps {
  buildingId: number
  onDirtyChange: ProviderDirtyChange
  tabs: ReactNode
}

interface CredentialsFormCardProps extends Omit<CredentialsCardProps, 'buildingId'> {
  configured: boolean
  fields: CredentialField[]
  isLoading: boolean
  isSaving: boolean
  onSave: (values: Record<string, string>) => Promise<unknown>
  // Note: only what the backend returns is shown once connected, secrets are never sent back
  savedValues?: Record<string, string>
}

export const CredentialsFormCard = ({
  configured,
  fields,
  isLoading,
  isSaving,
  onDirtyChange,
  onSave,
  savedValues,
  tabs,
}: CredentialsFormCardProps) => {
  const { t } = useTranslation(['organisation'])

  const schema = useMemo(
    () =>
      z.object(
        Object.fromEntries(
          fields.map(({ error, name }) => [name, z.string().trim().min(1, error)]),
        ) as Record<string, z.ZodString>,
      ),
    [fields],
  )

  const serverValues = useMemo(
    () => Object.fromEntries(fields.map(({ name }) => [name, savedValues?.[name] ?? ''])),
    [fields, savedValues],
  )

  const form = useForm<Record<string, string>>({
    defaultValues: serverValues,
    mode: 'onTouched',
    resolver: zodResolver(schema),
    values: serverValues,
  })

  const changeFields = useMemo<FormChangeFields<Record<string, string>>>(
    () =>
      Object.fromEntries(
        fields.map(({ label, name, secret }) => [
          name,
          { label, ...(secret && { format: maskSecret }) },
        ]),
      ),
    [fields],
  )

  const getChanges = useCallback(
    () =>
      getFormChanges({
        defaultValues: form.formState.defaultValues,
        fields: changeFields,
        values: form.getValues(),
      }),
    [changeFields, form],
  )

  const { cancel, isEditing, start, stop } = useProviderEdit({
    form,
    getChanges,
    onDirtyChange,
    serverValues,
  })

  const handleSave = form.handleSubmit(async (values) => {
    try {
      await onSave(values)
      toastFactory.success(t('organisation:places.integrations.keyless.toasts.saved'))
      stop()
    } catch (error) {
      toastFactory.error(getApiErrorMessage(error))
    }
  })

  const visibleFields = configured ? fields.filter(({ name }) => savedValues?.[name]) : fields

  return (
    <FormProvider {...form}>
      <ProviderCard
        actions={
          configured ? undefined : (
            <TabActions
              isDirty={form.formState.isDirty}
              isEditing={isEditing}
              isSaving={isSaving}
              onCancel={cancel}
              onEdit={start}
              onSave={handleSave}
            />
          )
        }
        configured={configured}
        loading={isLoading}
        notes={configured ? [t('organisation:places.accessControl.credentials.lockedNote')] : []}
        skeletonGroups={[{ labels: fields.map(({ label }) => label) }]}
        tabs={tabs}
      >
        {visibleFields.map(({ label, name, placeholder, secret }) => {
          const value = serverValues[name]

          return (
            <ProviderField
              key={name}
              required
              isEditing={isEditing}
              label={label}
              locked={configured}
              view={secret && value ? <SecretValue value={value} /> : value}
            >
              {secret ? (
                <PasswordInputFormField
                  autoComplete="new-password"
                  name={name}
                  placeholder={placeholder}
                  size="small"
                />
              ) : (
                <TextInputFormField
                  clearable
                  withRestore
                  autoComplete="off"
                  name={name}
                  placeholder={placeholder}
                  size="small"
                />
              )}
            </ProviderField>
          )
        })}
      </ProviderCard>
    </FormProvider>
  )
}
